import React, { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { TypedMemo } from '../common/types';
import { SMLRouteNames } from '../../routing/sml/SMLRoutes';
import { useSMLDatePickerDateParam } from './hooks/useSMLDatePickerDateParam';
import { isNavItemActive, smlHomeNavItem } from './navigation/smlNavigation';
import { smlNavigationChromeMessages as messages } from './navigation/smlNavigationMessages';
import { useCurrentNavScreen } from './navigation/hooks/useCurrentNavScreen';
import { SMLNavBadges } from './navigation/hooks/useNavBadges';
import { useSidebarScrollMemory } from './navigation/hooks/useSidebarScrollMemory';
import { useVisibleNavGroups } from './navigation/hooks/useVisibleNavGroups';
import { SidebarNavItem } from './navigation/SidebarNavItem';
import { SidebarGroup } from './navigation/SidebarGroup';
import { SidebarBrandRow } from './navigation/sidebar/SidebarBrandRow';
import { SidebarSearchField } from './navigation/sidebar/SidebarSearchField';
import { SidebarRailButton } from './navigation/sidebar/SidebarRailButton';
import { SidebarAccountCard } from './navigation/sidebar/SidebarAccountCard';
import { SidebarChecklistPill } from '../onboarding/checklist/SidebarChecklistPill';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole sidebar; each part uses its own slice of it */
import styles from './SMLSideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface SMLSideBarProps {
  badges: SMLNavBadges;
  /** The viewer chose the collapsed rail (1200 px and up). Below that the CSS forces the rail. */
  isRail: boolean;
  onToggleRail: () => void;
}

/**
 * The desktop navigation (design navigation-v2 1a): the brand row with the collapse button, the
 * search field (the command palette), Home, the four groups, then the first-release row and the
 * account card at the bottom. The account menu holds Settings, Connections, the plans, the theme,
 * the mobile app and Log out. From 769 to 1199 px, or by choice, it collapses to a 72 px rail
 * (1f). At 768 px and down it is hidden, and `MobileNav` takes over.
 */
const SMLSideBarComponent: React.FC<SMLSideBarProps> = ({ badges, isRail, onToggleRail }) => {
  const intl = useIntl();
  const { screen } = useCurrentNavScreen();
  const { selectedDateValue } = useSMLDatePickerDateParam();
  const navGroups = useVisibleNavGroups();
  const params = useMemo(() => ({ date: selectedDateValue }), [selectedDateValue]);
  // Each page mounts a new sidebar; this keeps the list where the reader left it.
  const navRef = useRef<HTMLElement>(null);
  const { onScroll } = useSidebarScrollMemory(navRef);
  // From 769 to 1199 px the CSS forces the rail; the checklist row must draw its rail form too.
  const isForcedRail = useMediaQuery({ query: '(min-width: 769px) and (max-width: 1199px)' });

  return (
    <aside className={clsx(styles.sidebar, isRail && styles.rail)} data-testid="sml-sidebar">
      <SidebarBrandRow isRail={isRail} onToggleRail={onToggleRail} params={params} />
      <SidebarSearchField isActive={screen === SMLRouteNames.browse} />

      <nav ref={navRef} onScroll={onScroll} className={styles.nav} aria-label={intl.formatMessage(messages.mainNav)}>
        <ul className={styles.list}>
          <SidebarNavItem item={smlHomeNavItem} isActive={isNavItemActive(smlHomeNavItem, screen)} params={params} />
          {navGroups.map((group) => (
            <SidebarGroup key={group.id} group={group} screen={screen} badges={badges} params={params} />
          ))}
        </ul>
      </nav>

      <div className={styles.bottom}>
        {/* design: navigation-v2 6d. One 38 px row; nothing when the checklist is done, hidden or for a member. */}
        <SidebarChecklistPill isRail={isRail || isForcedRail} />
        {isRail && <SidebarRailButton isRail onToggleRail={onToggleRail} />}
        <SidebarAccountCard accountAttention={badges.accountAttention} />
      </div>
    </aside>
  );
};

export const SMLSideBar = TypedMemo(SMLSideBarComponent);
